// S3 Upload utilities for PropWise using Amplify Storage.
//
// API-first upload flow: the Reports row is created server-side via
// POST /reports BEFORE any PDF upload. The server returns the
// `private/{identityId}/{reportId}/` prefix to upload PDFs to. PDFs are
// uploaded as pure file transfer with no identity-bearing object metadata —
// the row in DynamoDB is the source of truth for everything.

import { uploadData } from 'aws-amplify/storage';
import { FILE_SIZE, FILE_TYPES } from '../constants';

/**
 * Cancellable single-file upload task (used internally by the multi-file
 * flow). Public callers should use `uploadReports`.
 */
export interface UploadTask {
  /** Promise that resolves to the S3 path on successful upload. */
  result: Promise<string>;
  /** Cancel the upload. The result promise rejects with a cancellation error. */
  cancel: () => void;
}

/**
 * Cancellable multi-file upload task.
 */
export interface MultiUploadTask {
  /** Resolves when ALL files have uploaded; rejects on first failure / cancel. */
  result: Promise<string[]>;
  /** Cancel all in-flight uploads. */
  cancel: () => void;
}

/** Per-file upload progress, keyed by file name (0-100). */
export interface PerFileProgress {
  [fileName: string]: number;
}

/**
 * Per-file uploader for the multi-file flow.
 *
 * Key shape: `{uploadPrefix}{filename}` where `uploadPrefix` ends in '/' and
 * is the server-provided `private/{identityId}/{reportId}/` from POST /reports.
 */
const uploadOneToReportPrefix = (
  file: File,
  uploadPrefix: string,
  onProgress?: (progress: number) => void,
): UploadTask => {
  const safeName = file.name.replace(/[\\/]/g, '_');
  const normalizedPrefix = uploadPrefix.endsWith('/') ? uploadPrefix : `${uploadPrefix}/`;
  const path = `${normalizedPrefix}${safeName}`;

  const uploadTask = uploadData({
    path,
    data: file,
    options: {
      contentType: 'application/pdf',
      onProgress: ({ transferredBytes, totalBytes }) => {
        if (totalBytes && onProgress) {
          onProgress(Math.round((transferredBytes / totalBytes) * 100));
        }
      },
    },
  });

  const activeTask: { cancel: () => void } = uploadTask;
  const resultPromise = (async () => {
    const result = await uploadTask.result;
    return result.path;
  })();

  return {
    result: resultPromise,
    cancel: () => activeTask.cancel(),
  };
};

/**
 * Upload N PDFs under a server-provided per-report S3 prefix with per-file progress.
 *
 * @param files         PDFs to upload (≤ MAX_FILES_PER_REPORT, total ≤ MAX_TOTAL_UPLOAD_BYTES).
 * @param uploadPrefix  Server-provided prefix `private/{identityId}/{reportId}/`.
 *                      Trailing slash is added if missing.
 * @param onProgress    Called with a fresh PerFileProgress map after every progress tick.
 * @returns MultiUploadTask resolving to the list of S3 paths.
 * @throws Error for validation failures (sync, before returning the task).
 */
export const uploadReports = (
  files: File[],
  uploadPrefix: string,
  onProgress?: (progress: PerFileProgress) => void,
): MultiUploadTask => {
  if (!files.length) {
    throw new Error('No files provided');
  }
  if (files.length > FILE_SIZE.MAX_FILES_PER_REPORT) {
    throw new Error(
      `Cannot upload more than ${FILE_SIZE.MAX_FILES_PER_REPORT} files per report`,
    );
  }

  let totalBytes = 0;
  for (const f of files) {
    if (f.type !== FILE_TYPES.PDF) {
      throw new Error(`Only PDF files are allowed (${f.name})`);
    }
    if (f.size > FILE_SIZE.MAX_UPLOAD_BYTES) {
      throw new Error(
        `Each file must be under ${FILE_SIZE.MAX_UPLOAD_MB}MB (${f.name})`,
      );
    }
    totalBytes += f.size;
  }
  if (totalBytes > FILE_SIZE.MAX_TOTAL_UPLOAD_BYTES) {
    throw new Error(
      `Total upload size must be under ${FILE_SIZE.MAX_TOTAL_UPLOAD_MB}MB`,
    );
  }

  const progress: PerFileProgress = {};
  files.forEach((f) => {
    progress[f.name] = 0;
  });

  const tasks: UploadTask[] = files.map((file) =>
    uploadOneToReportPrefix(file, uploadPrefix, (pct) => {
      progress[file.name] = pct;
      onProgress?.({ ...progress });
    }),
  );

  const resultPromise = Promise.all(tasks.map((t) => t.result));

  return {
    result: resultPromise,
    cancel: () => tasks.forEach((t) => t.cancel()),
  };
};
